import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  setSelectedRow,
  clearUnreadProject,
  nextPage,
  openSortConfirm,
  closeSortConfirm,
  openDeleteConfirm,
  closeDeleteConfirm,
  openNoPermission,
  setSort,
  setPage,
} from 'app/slices/dashboardSlice';
import { useColumns, useCurrentUser } from 'hooks/useDashboardData';
import { useDashboardProjects, useEffectiveFilters, isFilterActive } from 'hooks/useDashboardProjects';
import { useLookupMaps } from 'hooks/useLookupMaps';
import { useRowContextMenu } from 'hooks/useRowContextMenu';
import { useRowDragReorder } from 'hooks/useRowDragReorder';
import { dashboardApi } from 'app/services/dashboardApi';
import type { BoardPermission, IDashboardProject } from 'models';
import { orderedVisibleColumns } from '../dashboardUtils';
import ColumnHeaderRow, { FilterInput } from './ColumnHeaderRow';
import ProjectRow from './ProjectRow';
import Infobar from './Infobar';
import SortConfirm from './SortConfirm';
import DeleteProjectConfirm from './DeleteProjectConfirm';
import { track } from 'app/analytics';

const {
  useUpdateUserMutation,
  useRenameProjectMutation,
  useDeleteProjectMutation,
  useDuplicateProjectMutation,
  useSetCustomOrderMutation,
} = dashboardApi;

/** Distance (px) from the bottom that triggers loading the next page (§3.E). */
const SCROLL_THRESHOLD = 500;

interface ProjectTableProps {
  permission: BoardPermission;
}

/**
 * The project table (§3.E) — fixed first column + horizontally-scrollable
 * dynamic columns, pinned + default row lists, per-column sort/search, infinite
 * scroll, selection + keyboard nav, and the infobar.
 *
 * Mounts INTO the existing #list_heading / #list_heading_noname / .list-rows
 * structure rendered by DashboardShell. This component owns the scroll container
 * (.admin-table.first-fixed-list) interactions.
 */
const ProjectTable = ({ permission }: ProjectTableProps) => {
  const dispatch = useAppDispatch();
  const { data: columns } = useColumns();
  const { data: user } = useCurrentUser();
  const [updateUser] = useUpdateUserMutation();
  const [renameProject] = useRenameProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [duplicateProject] = useDuplicateProjectMutation();
  const [setCustomOrder] = useSetCustomOrderMutation();
  const maps = useLookupMaps();

  const manageColumns = useAppSelector((s) => s.dashboard.manageColumns);
  const selectedRowId = useAppSelector((s) => s.dashboard.selectedRowId);
  const unreadProjectIds = useAppSelector((s) => s.dashboard.unreadProjectIds);
  const sortBy = useAppSelector((s) => s.dashboard.sortBy);
  const sortOrder = useAppSelector((s) => s.dashboard.sortOrder);
  const isManual = useAppSelector((s) => s.dashboard.isManual);
  const sortConfirmField = useAppSelector((s) => s.dashboard.modals.sortConfirm);
  const deleteConfirmId = useAppSelector((s) => s.dashboard.modals.deleteConfirm);

  const filters = useEffectiveFilters();
  const filterActive = isFilterActive(filters);

  // `rows`/`pinnedRows` stay empty until BOTH list queries have settled, so the
  // two lists — and the infobar counts derived from them — appear together.
  const { rows, pinnedRows, total, hasMore, isLoading, isFetching, listsReady } =
    useDashboardProjects();

  const visibleColumns = useMemo(
    () => orderedVisibleColumns(columns, manageColumns),
    [columns, manageColumns]
  );

  const restricted = permission !== 'access';

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);

  // Right-click context menu over the scroll container (parity 80-82).
  useRowContextMenu(scrollRef as React.RefObject<HTMLElement>, true);

  // ---- Selection + open ----
  // selectProject: select the row + clear its unread highlight (parity 71/78).
  const onSelect = useCallback(
    (id: number) => {
      dispatch(setSelectedRow(id));
      if (unreadProjectIds.includes(id)) dispatch(clearUnreadProject(id));
    },
    [dispatch, unreadProjectIds]
  );
  const onOpen = useCallback(
    (id: number) => {
      if (permission === 'no_access') {
        // §3.E / §3.K / parity 76: opening a no-access project shows the
        // "No Permission!" popup instead of navigating.
        dispatch(openNoPermission());
        return;
      }
      window.open(`/project/${id}#project`, '_self');
    },
    [permission, dispatch]
  );

  // ---- Row actions (parity 62-68) ----
  // Rename → renameProject PATCH (≥3-char guard enforced in the row).
  const onRename = useCallback(
    (id: number, name: string) => {
      renameProject({ id, name });
    },
    [renameProject]
  );

  // Duplicate → POST /project/duplicate, name "{name} (n)" auto-incremented from
  // the currently-loaded rows; preloader shown (project.js duplicate_project).
  const onDuplicate = useCallback(
    (project: IDashboardProject) => {
      const baseName = project.name ?? '';
      let duplicateMax = 1;
      const all = [...pinnedRows, ...rows];
      all.forEach((item) => {
        const iname = item.name ?? '';
        if (iname.indexOf(baseName) === 0) {
          const str = iname.replace(baseName, ' ');
          const m = /\((\d+)\)/.exec(str);
          if (m != null) {
            const n = parseInt(m[1], 10);
            if (n > duplicateMax) duplicateMax = n;
          }
        }
      });
      const newName = `${baseName} (${duplicateMax + 1})`;
      // Preloader (project.js: `$('#process_dotted_preloader').addClass('active')`).
      document.getElementById('process_dotted_preloader')?.classList.add('active');
      // Counted when the server accepts it: the copy itself runs in Celery and
      // the dashboard gets no completion signal (the list just refetches).
      duplicateProject({ project_id: project.id, project_name: newName }).then((res) => {
        if ('data' in res) track('project_duplicated');
      });
    },
    [pinnedRows, rows, duplicateProject]
  );

  // Pin/Unpin → setCustomOrder ({project_id, dashboard_pinned: !pinned}); the
  // refetch moves it to/from the pinned list top + updates counts (parity 67/68).
  const onPin = useCallback(
    (project: IDashboardProject, pinned: boolean) => {
      setCustomOrder({
        project_custom_order: [{ project_id: project.id, dashboard_pinned: !pinned }],
      });
    },
    [setCustomOrder]
  );

  // Delete → open the per-row DeleteProjectConfirm (parity 64).
  const onRequestDelete = useCallback(
    (id: number) => dispatch(openDeleteConfirm(id)),
    [dispatch]
  );

  // Confirm delete → DELETE; tag invalidation refetches Projects/PinnedProjects,
  // which recomputes the server `count` (decrement) + re-counts the rendered rows
  // → the infobar Total/Filtered refresh automatically (parity 65).
  const deleteTarget = useMemo(
    () => [...pinnedRows, ...rows].find((p) => p.id === deleteConfirmId) ?? null,
    [pinnedRows, rows, deleteConfirmId]
  );
  const confirmDelete = useCallback(() => {
    if (deleteConfirmId == null) return;
    deleteProject(deleteConfirmId)
      .unwrap()
      .then(() => track('project_deleted'))
      .catch(() => undefined);
    dispatch(closeDeleteConfirm());
  }, [deleteConfirmId, deleteProject, dispatch]);
  const cancelDelete = useCallback(() => dispatch(closeDeleteConfirm()), [dispatch]);

  // ---- Drag-reorder commit (parity 69/70) ----
  // First manual reorder switches the user's default-order to 'manual' (clears
  // the active column sort highlight + shows "User Custom Sorted"); subsequent
  // reorders just persist. Then POST custom_order with dashboard_order entries.
  const onReorderCommit = useCallback(
    (entries: { project_id: number; dashboard_order: number }[]) => {
      const persist = () =>
        setCustomOrder({ project_custom_order: entries });
      if (!isManual) {
        // Switch sort mode → manual: clear sort highlight, infobar "User Custom
        // Sorted", and PATCH the user, then persist the order.
        dispatch(setSort({ sortBy: null, isManual: true }));
        if (user?.id != null) {
          updateUser({ id: user.id, body: { dashboard_projects_default_order: 'manual' } })
            .unwrap()
            .then(persist)
            .catch(persist);
        } else {
          persist();
        }
      } else {
        persist();
      }
    },
    [isManual, dispatch, user?.id, updateUser, setCustomOrder]
  );

  // Drag-reorder the DEFAULT (un-pinned) list when NO filter is active
  // (`.no-drag` otherwise). Pinned rows are not drag-reorderable.
  const {
    rows: orderedRows,
    dragProps,
    draggingId,
  } = useRowDragReorder({
    rows,
    disabled: filterActive,
    onCommit: onReorderCommit,
  });

  // Auto-select the first row on initial render if nothing is selected (parity 72).
  // Gated on `listsReady`: the pinned query settles independently of the main
  // one, and selecting early would grab the first UNPINNED row (OLD selects the
  // first pinned row when one exists).
  useEffect(() => {
    if (selectedRowId != null || !listsReady) return;
    const first = pinnedRows[0]?.id ?? rows[0]?.id;
    if (first != null) dispatch(setSelectedRow(first));
  }, [selectedRowId, listsReady, pinnedRows, rows, dispatch]);

  // ---- Keyboard nav (Up/Down move selection across pinned↔default; Enter opens) ----
  // Uses orderedRows (the drag-reordered visual order) so Up/Down follow what
  // is on screen — a drag-reorder no longer refetches the lists (QA #4), so
  // the raw `rows` order can be stale after a drag.
  const allRows: IDashboardProject[] = useMemo(
    () => [...pinnedRows, ...orderedRows],
    [pinnedRows, orderedRows]
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        // Skip when focus is in a button/select/textarea or a NON-filter input.
        if (tag === 'BUTTON' || tag === 'SELECT' || tag === 'TEXTAREA') return;
        if (tag === 'INPUT' && !target.classList.contains('filter')) return;
      }
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'Enter') return;
      if (allRows.length === 0) return;

      if (e.key === 'Enter') {
        if (selectedRowId != null) {
          // Enter in a .filter input blurs it then opens the selected row.
          if (target?.classList.contains('filter')) target.blur();
          onOpen(selectedRowId);
        }
        return;
      }

      e.preventDefault();
      const idx = allRows.findIndex((r) => r.id === selectedRowId);
      let nextIdx = idx;
      if (e.key === 'ArrowDown') nextIdx = Math.min(allRows.length - 1, idx + 1);
      if (e.key === 'ArrowUp') nextIdx = Math.max(0, idx === -1 ? 0 : idx - 1);
      const nextRow = allRows[nextIdx];
      if (nextRow) {
        dispatch(setSelectedRow(nextRow.id));
        // Scroll the newly-selected row into view.
        const el = scrollRef.current?.querySelector(
          `li.project-li[data-project-id="${nextRow.id}"]`
        );
        el?.scrollIntoView({ block: 'nearest' });
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [allRows, selectedRowId, dispatch, onOpen]);

  // ---- Infinite scroll + fixed-column / table-buttons / hover reposition ----
  const reposition = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const width = container.clientWidth;
    const scrollWidth = container.scrollWidth;
    const atEnd = width + scrollLeft >= scrollWidth - 1;

    // Fixed first column tracks scrollLeft (parity 185/188).
    container.querySelectorAll<HTMLElement>('.fixed-column').forEach((el) => {
      el.style.left = `${scrollLeft}px`;
    });
    // table-buttons right-anchored within the viewport edge; scroll-end at far
    // right; hover left offset (parity 186/187/189/190/191).
    container.querySelectorAll<HTMLElement>('.table-buttons').forEach((el) => {
      el.style.left = `${width + scrollLeft - 55}px`;
      el.classList.toggle('scroll-end', atEnd);
    });
    container.querySelectorAll<HTMLElement>('.hover').forEach((el) => {
      el.style.left = `${scrollLeft}px`;
    });
  }, []);

  const onScroll = useCallback(() => {
    reposition();
    const container = scrollRef.current;
    if (!container) return;
    // Infinite scroll: within 500px of the bottom + page1 full + more pages.
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (
      remaining <= SCROLL_THRESHOLD &&
      hasMore &&
      rows.length >= 100 &&
      !loadingRef.current
    ) {
      loadingRef.current = true;
      dispatch(nextPage());
    }
  }, [reposition, hasMore, rows.length, dispatch]);

  // Release the loading guard once a fetch settles (rows changed) (§3.E guard).
  useEffect(() => {
    loadingRef.current = false;
  }, [rows.length]);

  // Reposition synchronously after every (re)render + on resize (no scroll hack).
  useLayoutEffect(() => {
    reposition();
  });

  // ---- Preloader lifecycle (parity 174/180-182 + QA D3/D5) ----
  // Legacy semantics (projects.js): EVERY list fetch shows #whiteboard_preloader
  // (line 194) and EVERY render-end schedules removal of both preloaders
  // (lines 229/242) — including after create/duplicate, where the collection
  // refetch's render is what clears #process_dotted_preloader. So the lifecycle
  // must key off FETCH CYCLES, not row counts: a refetch that returns the same
  // number of rows (full page) previously never re-ran this effect, leaving the
  // create/duplicate spinner active forever (QA D5 — reproduced on dev3 with
  // sockets connected; not a socket issue).
  const prevPinnedHadContent = useRef(false);
  // QA D3: show the list spinner while fetching/filtering (legacy line 194) —
  // unless the process preloader already covers the board (create/duplicate).
  useEffect(() => {
    if (!isFetching) return;
    const processActive = document
      .getElementById('process_dotted_preloader')
      ?.classList.contains('active');
    if (!processActive) {
      document.getElementById('whiteboard_preloader')?.classList.add('active');
    }
  }, [isFetching]);
  useEffect(() => {
    // `!listsReady` keeps the preloader up through the gate: legacy schedules the
    // removal from the END of renderProjects (projects.js:228), i.e. after the
    // rows are in the DOM, never while the board is still blank.
    if (isLoading || isFetching || !listsReady) return;
    // 200ms if the pinned list already had content at render start, else 1500ms.
    const delay = prevPinnedHadContent.current ? 200 : 1500;
    const t1 = setTimeout(() => {
      document.getElementById('process_dotted_preloader')?.classList.remove('active');
    }, delay);
    const t2 = setTimeout(() => {
      document.getElementById('whiteboard_preloader')?.classList.remove('active');
    }, 350);
    prevPinnedHadContent.current = pinnedRows.length > 0;
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isLoading, isFetching, listsReady, pinnedRows.length, rows.length]);

  useEffect(() => {
    const onResize = () => reposition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [reposition]);

  // ---- Sort ----
  const onRequestSort = useCallback(
    (field: string) => dispatch(openSortConfirm(field)),
    [dispatch]
  );

  // The order that WOULD apply: toggle if same field, else asc.
  const nextSortOrder: 'asc' | 'desc' =
    sortConfirmField === sortBy ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';

  const confirmSort = useCallback(async () => {
    if (!sortConfirmField) return;
    const order = nextSortOrder;
    const orderStr = order === 'desc' ? `-${sortConfirmField}` : sortConfirmField;
    dispatch(setSort({ sortBy: sortConfirmField, sortOrder: order, isManual: false }));
    dispatch(closeSortConfirm());
    if (user?.id != null) {
      await updateUser({ id: user.id, body: { dashboard_projects_default_order: orderStr } });
      // Rewind to page 1 AFTER the pref is saved (legacy container.js:277-281
      // does the same: `user.save(...).then(() => render(true))`, and
      // render() calls resetPagination()). The sort lives on the user record,
      // not in the list URL, so the page args do not change on their own — and
      // useDashboardProjects only REPLACES its accumulated rows when the
      // settled response is page 1. Scroll past the first page, then sort, and
      // the invalidation refetch of page N lands on the `fetchedPage <=
      // lastPageRef` branch and is dropped: the table keeps rendering the
      // PREVIOUS order's rows under the new column's caret. Resetting the page
      // here puts it back on the page-1 replace path.
      dispatch(setPage(1));
    }
  }, [sortConfirmField, nextSortOrder, dispatch, user?.id, updateUser]);

  const cancelSort = useCallback(() => dispatch(closeSortConfirm()), [dispatch]);

  // ---- Fixed Project Name header cell sort label ----
  const nameSelected = sortBy === 'name';
  const nameCaret = nameSelected ? (sortOrder === 'asc' ? 'up' : 'down') : 'down';

  const renderedCount = pinnedRows.length + rows.length;

  return (
    <>
      <div className="admin-table first-fixed-list" ref={scrollRef} onScroll={onScroll}>
        <ul className="list-heading" id="list_heading">
          <li className="master-list-heading fixed-column">
            <ul>
              <li>
                <div className="project-header">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    className={`sorting-btn for-project name-label${nameSelected ? ' selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => onRequestSort('name')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onRequestSort('name');
                    }}
                  >
                    <h4>
                      Project Name
                      <i className={`icon-arrow-${nameCaret}-1`} />
                    </h4>
                  </label>
                </div>
                <FilterInput field="name" withDataId={false} />
              </li>
            </ul>
          </li>
          <li>
            <ul id="list_heading_noname">
              <ColumnHeaderRow columns={visibleColumns} onRequestSort={onRequestSort} />
            </ul>
          </li>
        </ul>

        <ul className="list-rows project-list-rows pinned">
          {pinnedRows.map((p) => (
            <ProjectRow
              key={`pin-${p.id}`}
              project={p}
              columns={visibleColumns}
              maps={maps}
              pinned
              selected={selectedRowId === p.id}
              filtered={filterActive}
              restrictedPermission={restricted}
              unread={unreadProjectIds.includes(p.id)}
              onSelect={onSelect}
              onOpen={onOpen}
              onRename={onRename}
              onDuplicate={onDuplicate}
              onPin={onPin}
              onRequestDelete={onRequestDelete}
            />
          ))}
        </ul>

        {/* `.no-drag` when a filter is active disables drag-reorder (parity 48/69). */}
        <ul
          className={`list-rows project-list-rows list-rows-default${
            filterActive ? ' no-drag' : ''
          }`}
        >
          {orderedRows.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              columns={visibleColumns}
              maps={maps}
              pinned={false}
              selected={selectedRowId === p.id}
              filtered={filterActive}
              restrictedPermission={restricted}
              unread={unreadProjectIds.includes(p.id)}
              onSelect={onSelect}
              onOpen={onOpen}
              onRename={onRename}
              onDuplicate={onDuplicate}
              onPin={onPin}
              onRequestDelete={onRequestDelete}
              dragProps={filterActive ? undefined : dragProps(p.id)}
              dragging={draggingId === p.id}
            />
          ))}
        </ul>

        {/* No empty-state copy: the Backbone dashboard renders a blank area when
            the list is empty (verified vs OLD — archived view shows nothing). */}
      </div>

      <div className="helper min-height-50" id="infobar">
        <Infobar total={total} renderedCount={renderedCount} columns={columns} />
      </div>

      {sortConfirmField && (
        <SortConfirm
          field={sortConfirmField}
          nextOrder={nextSortOrder}
          columns={columns}
          onConfirm={confirmSort}
          onCancel={cancelSort}
        />
      )}

      {deleteConfirmId != null && deleteTarget && (
        <DeleteProjectConfirm
          models={[deleteTarget]}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </>
  );
};

export default ProjectTable;
